/**
 * Global Invoicing repository (iter294) — /invoicing, directly below
 * Purchasing in the Operations nav.
 *
 * Read-and-act only: search / review / resend / remind / record payment.
 * Creating and editing an invoice still happens inside the work order —
 * every link here lands you there.
 */
import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from "react";
import { tr } from "@/i18n";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, BellRing, X, Loader2, Mail, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  getInvoicingList, invoicingExportUrl, bulkInvoiceReminder,
  getFoundationPaid, updateInvoice,
} from "@/lib/api";
import { setFocusTarget, FOCUS_INVOICE } from "@/lib/deepLinkFocus";
import { useAuth } from "@/auth/AuthContext";
import SearchBar from "@/components/common/SearchBar";
import { SummaryTile } from "@/components/common/SummaryTile";
import { PillRail, Pill, PillDivider } from "@/components/common/Segmented";
import { AR_COLUMNS, InlineDate, LienCell, LienFlag, ColToggle, InvoiceNotesCell, LienAutoBell, fmtRetainage, LIEN_STATUS_LABEL } from "@/components/invoicing/ARCells";
import { gridNavProps } from "@/lib/gridNav";
import { LienReminderDialog } from "@/components/invoicing/LienReminderDialog";
import { LienChecklistDialog } from "@/components/invoicing/LienChecklistDialog";
import { InvoiceActionButtons, sendInvoiceReminder, PaymentDialog } from "@/components/invoicing/InvoiceActions";
import StickyTop from "@/components/common/StickyTop";

import { btn } from "@/components/common/btn";

import { shortDate, money } from "@/lib/format";
import { SortableHeader, HeaderFilter } from "@/components/common/SortHeader";
const LIEN_STATUS_OPTIONS = [{ value: "none", label: "No status" }, ...Object.entries(LIEN_STATUS_LABEL).filter(([k]) => k).map(([value, label]) => ({ value, label }))];
const errMsg = (e) => e?.response?.data?.detail || e?.message || "Failed";

const STATUS_PILL = {
  draft: "bg-amber-100 text-amber-800 border-amber-200",
  sent: "bg-sky-100 text-sky-800 border-sky-200",
  paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  void: "bg-slate-100 text-slate-500 border-slate-200",
};
const STATUS_LABEL = { draft: "Created", sent: "Sent", paid: "Paid", void: "Void" };
const AGE_PILLS = [
  { key: "sent", label: "+0", title: "Sent, any age" },
  { key: "30_plus", label: "+30", title: "More than 30 days since the invoice date" },
  { key: "60_plus", label: "+60", title: "More than 60 days" },
  { key: "90_plus", label: "+90", title: "More than 90 days" },
];

const FOUNDATION_PILL = {
  success: ["Pushed", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  blocked: ["Blocked", "bg-rose-50 text-rose-700 border-rose-200"],
  failed: ["Failed", "bg-rose-50 text-rose-700 border-rose-200"],
  bridge_error: ["Bridge error", "bg-rose-50 text-rose-700 border-rose-200"],
};

const CLOSED_STATUSES = ["paid", "void"];
const STATUS_FILTERS = [
  { value: "__all__", label: "All Statuses" },
  { value: "draft", label: "Created" },
  { value: "sent", label: "Sent" },
  { value: "partial", label: "Partial Payment" },
  { value: "paid", label: "Paid" },
  { value: "void", label: "Void" },
  { value: "foundation_pushed", label: "Foundation Pushed" },
  { value: "foundation_blocked", label: "Foundation Blocked" },
  { value: "foundation_not_pushed", label: "Foundation Not Pushed" },
];
const CLOSED_STATUS_FILTERS = [
  { value: "__all__", label: "Paid / Void" },
  { value: "paid", label: "Paid" },
  { value: "void", label: "Void" },
];

// Aging = days since the invoice date; a fresh Net 30 invoice lives in 0–30.
// Age cell: amber 30–40 days, red text past 40.
const ageTone = (d) => (d == null ? "text-slate-600" : d > 40 ? "text-rose-600 font-semibold" : d >= 30 ? "text-amber-600 font-semibold" : "text-slate-600");

const BUCKETS = ["0_30", "31_60", "61_90", "90_plus"];
const BUCKET_LABEL = { "0_30": "0–30", "31_60": "31–60", "61_90": "61–90", "90_plus": "90+" };
// Aging warning colours — 61–90 amber, 90+ rose (tiles and group headers).
const BUCKET_TONE = { "61_90": "amber", "90_plus": "rose" };
const BUCKET_HEADER = { "61_90": "text-amber-700", "90_plus": "text-rose-700" };

// Remind now · Auto reminders · Record payment. Foundation sync status rides along in the tooltip only.
function RowActions({ r, ...props }) {
  const fdn = r.foundation_ar_status ? `Foundation: ${(FOUNDATION_PILL[r.foundation_ar_status] || [r.foundation_ar_status])[0]}` : "Not pushed to Foundation yet";
  return <TableCell className="text-right whitespace-nowrap" title={fdn} data-testid={`foundation-${r.id}`}><InvoiceActionButtons r={r} {...props} /></TableCell>;
}

// Lien Actions — email the lien rights notice now (mail), automate it (bell), filing checklist (clipboard).
function LienActions({ r, canWrite, closed, onEmail, onChecklist, onChanged }) {
  const ready = !!r.lien_pay_by && r.balance > 0 && !closed;
  const title = !ready
    ? (r.balance <= 0 || closed ? "Nothing outstanding" : "Set the last day of work first — the lien dates drive the pay-by date")
    : `Email lien rights notice — pay by ${shortDate(r.lien_pay_by)}${r.lien_reminder_sent_at ? ` · last sent ${shortDate(r.lien_reminder_sent_at)}` : ""}`;
  const intentFiled = !!r.lien_filings?.intent?.filed, lienFiled = !!r.lien_filings?.lien?.filed;
  const clipTone = lienFiled ? "text-emerald-700" : intentFiled ? "text-amber-700" : "text-slate-400";
  const clipTitle = lienFiled ? `Lien filed ${shortDate(r.lien_filings.lien.filed_on)}${r.lien_filings.lien.county ? ` · ${r.lien_filings.lien.county} County` : ""} — open checklist`
    : intentFiled ? `Notice of Intent filed ${shortDate(r.lien_filings.intent.filed_on)} — lien not yet filed` : "Lien filing checklist — Intent and Lien filings";
  return (
    <TableCell className="text-right whitespace-nowrap">
      <Button size="sm" variant="ghost" className={`h-7 w-7 p-0 ${r.lien_reminder_sent_at ? "text-amber-700" : ""}`} title={title}
        aria-label="Email lien rights notice" onClick={() => onEmail(r)} disabled={!canWrite || !ready} data-testid={`btn-lien-email-${r.id}`}>
        <Mail className="w-3.5 h-3.5" />
      </Button>
      <LienAutoBell row={r} disabled={!canWrite || closed} onChanged={onChanged} />
      <Button size="sm" variant="ghost" className={`h-7 w-7 p-0 ${clipTone}`} title={clipTitle} aria-label="Lien filing checklist"
        onClick={() => onChecklist(r)} disabled={!canWrite} data-testid={`btn-lien-checklist-${r.id}`}>
        <ClipboardCheck className="w-3.5 h-3.5" />
      </Button>
      {r.lien_reminder_count > 0 && <span className="ml-0.5 text-[10px] text-amber-700 num" data-testid={`lien-email-count-${r.id}`}>{r.lien_reminder_count}</span>}
    </TableCell>
  );
}



export default function Invoicing() {
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const canWrite = can("ops.invoices", "write");

  const [rows, setRows] = useState([]);
  const [fpaid, setFpaid] = useState({});   // iter327 — Foundation invoice_no → paid snapshot
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");       // column filter (header menu)
  const [customerId, setCustomerId] = useState(""); // column filter (header menu)
  const [lienStatus, setLienStatus] = useState([]); // column filter (header menu, multi)
  const prefKey = `invoicing.prefs.${user?.id || user?.email || "anon"}`;
  const saved = useMemo(() => { try { return JSON.parse(localStorage.getItem(prefKey)) || {}; } catch { return {}; } }, [prefKey]);
  const [agePill, setAgePill] = useState(saved.agePill || "");     // sent | 30_plus | 60_plus | 90_plus
  const [view, setView] = useState("open");       // open | paid | void (never remembered)
  const [groupDue, setGroupDue] = useState(!!saved.groupDue);
  const [showLiens, setShowLiens] = useState(!!saved.showLiens);
  const [showNotes, setShowNotes] = useState(!!saved.showNotes);
  const activeColumns = showLiens ? AR_COLUMNS : AR_COLUMNS.slice(0, AR_COLUMNS.findIndex(c => c.key === "lien_status") + 1);
  const patchRow = async (r, fields) => {
    try { await updateInvoice(r.id, fields); load(params); }
    catch (e) { toast.error(errMsg(e)); }
  }; // "Due" = the open list split into aging sections
  const [sort, setSort] = useState(saved.sort || "invoice_age_days");
  const [dir, setDir] = useState(saved.dir || "desc");
  useEffect(() => { localStorage.setItem(prefKey, JSON.stringify({ agePill, groupDue, sort, dir, showLiens, showNotes })); }, [prefKey, agePill, groupDue, sort, dir, showLiens, showNotes]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [selected, setSelected] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [lienTarget, setLienTarget] = useState(null);
  const [checklistTarget, setChecklistTarget] = useState(null);
  const debounce = useRef(null);
  const seenCustomers = useRef(new Map()); // every customer seen so far — the dropdown never shrinks behind a filter

  const closed = view !== "open";
  const bucket = ["30_plus", "60_plus", "90_plus"].includes(agePill) ? agePill : "";
  // Grouping by Due reads the whole open list (API max 500) so the section totals match the tiles.
  const effectivePageSize = groupDue && !closed ? 500 : pageSize;
  const params = useMemo(() => {
    const searching = !!(q && q.trim());
    // A search looks across every view (open + paid + void); pills only scope the un-searched list.
    const statuses = status ? [status] : searching ? [] : closed ? [view] : agePill === "sent" ? ["sent"] : [];
    return {
      q, show_all: searching || closed || CLOSED_STATUSES.includes(status), sort, dir, page, page_size: effectivePageSize,
      ...(statuses.length ? { status: statuses } : {}),
      ...(customerId ? { customer_id: customerId } : {}),
      ...(lienStatus.length ? { lien_status: lienStatus } : {}),
      ...(bucket && !searching ? { bucket } : {}),
    };
  }, [q, closed, view, agePill, sort, dir, page, effectivePageSize, status, customerId, bucket, lienStatus]);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const { data } = await getInvoicingList(p);
      setRows(data.rows || []);
      const fnums = (data.rows || []).map((r) => r.foundation_ar_invoice_no || r.foundation_invoice_number).filter(Boolean);
      if (fnums.length) getFoundationPaid(fnums).then((r) => setFpaid(r.data || {})).catch(() => {});
      setSummary(data.summary || null);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  // Search-as-you-type: debounce the request, never remount the table.
  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(params), 250);
    return () => clearTimeout(debounce.current);
  }, [params, load]);

  useEffect(() => { setPage(1); }, [q, status, customerId, lienStatus, agePill, view, groupDue]);

  const customers = useMemo(() => {
    rows.forEach(r => { if (r.customer_id && !seenCustomers.current.has(r.customer_id)) seenCustomers.current.set(r.customer_id, r.customer_name); });
    return [...seenCustomers.current.entries()].sort((a, b) => (a[1] || "").localeCompare(b[1] || ""));
  }, [rows]);

  // Aging sections, always in order 0–30 · 31–60 · 61–90 · 90+ (an empty band still shows its header) with count and balance.
  const groups = useMemo(() => {
    if (!groupDue || closed) return [{ key: "", rows }];
    const by = {};
    rows.forEach((r) => { const k = r.aging_bucket || "none"; (by[k] = by[k] || []).push(r); });
    return [...BUCKETS, "none"].filter((k) => k !== "none" || by[k]?.length)
      .map((k) => ({ key: k, label: k === "none" ? "No aging" : `${BUCKET_LABEL[k]} days`, rows: by[k] || [] }));
  }, [rows, groupDue, closed]);

  const toggleSort = (key) => {
    if (sort === key) setDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSort(key); setDir(key === "customer_name" || key === "job_name" ? "asc" : "desc"); }
  };

  const pickView = (v) => { setView(v); setGroupDue(false); setStatus(""); setAgePill(""); setSelected([]); };

  const openWO = (r, focusInvoice) => {
    if (!r.work_order_id) { toast.error("This invoice's work order no longer exists"); return; }
    if (focusInvoice) setFocusTarget(FOCUS_INVOICE, r.id);
    navigate("/clients", {
      state: { openClientId: r.customer_id, openTab: "workorders", openWorkOrderId: r.work_order_id },
    });
  };

  const remind = async (r) => { if (await sendInvoiceReminder(r)) load(params); };

  const allSelected = rows.length > 0 && selected.length === rows.length;
  const selectedRows = rows.filter(r => selected.includes(r.id));
  const filtersOn = !!(q || status || customerId || agePill || lienStatus.length);
  const statusHeaderOptions = (closed ? CLOSED_STATUS_FILTERS : STATUS_FILTERS).filter((o) => o.value !== "__all__").map((o) => ({ value: o.value, label: o.label }));
  const customerHeaderOptions = customers.map(([id, name]) => ({ value: id, label: name || id }));
  const colSpan = activeColumns.length + 1;

  return (
    <div className="space-y-3 pb-24" data-testid="invoicing-page">
      <StickyTop testId="invoicing-sticky-top" className="!pb-[6px]">
      {summary && !closed && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="invoicing-summary">
          <SummaryTile testId="tile-open" label={tr("Open Invoices")} value={summary.open_count}
            hint={money(summary.open_total)} />
          <SummaryTile testId="tile-lien-watch" tone={summary.lien_watch_count ? "amber" : "slate"} label={tr("Lien Watch")}
            value={summary.lien_watch_count || 0} hint="within 30 days of intent" />
          {BUCKETS.map(b => (
            <SummaryTile
              key={b} testId={`tile-bucket-${b}`} tone={BUCKET_TONE[b] || "slate"}
              label={BUCKET_LABEL[b]} value={summary.buckets?.[b]?.count ?? 0}
              hint={money(summary.buckets?.[b]?.total ?? 0)}
            />
          ))}
        </div>
      )}

      {/* Toolbar — search · All / Due | Paid / Void · Status · Customer · export · count far right */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 !mt-[18px]" data-testid="invoicing-toolbar">
        <SearchBar value={q} onChange={setQ} testId="input-invoicing-search" placeholder={tr("Search...")} />
        <PillRail testId="invoicing-aging-pills">
          <Pill on={!closed && groupDue} onClick={() => { if (closed) pickView("open"); setGroupDue(!groupDue); setAgePill(""); }} testId="aging-pill-due"
            title="Group open invoices by age: 0–30 · 31–60 · 61–90 · 90+">{tr("Aging")}</Pill>
          {AGE_PILLS.map((p) => (
            <Pill key={p.key} on={!closed && agePill === p.key} testId={`aging-pill-${p.key}`} title={p.title}
              onClick={() => { if (closed) pickView("open"); setGroupDue(false); setAgePill(agePill === p.key ? "" : p.key); }}>{tr(p.label)}</Pill>
          ))}
          <PillDivider />
          <Pill on={view === "paid"} onClick={() => pickView(view === "paid" ? "open" : "paid")} testId="invoicing-view-paid" title={tr("Paid invoices")}>{tr("Paid")}</Pill>
          <Pill on={view === "void"} onClick={() => pickView(view === "void" ? "open" : "void")} testId="invoicing-view-void" title={tr("Void invoices")}>{tr("Void")}</Pill>
        </PillRail>
        {filtersOn && (
          <button type="button" className={btn("nav")} data-testid="btn-clear-invoicing-filters"
            onClick={() => { setQ(""); setStatus(""); setCustomerId(""); setLienStatus([]); setAgePill(""); }}>
            <X className="w-3.5 h-3.5" /> {tr("Clear")}
          </button>
        )}
        <button type="button" className={btn("data")} onClick={() => { window.location.href = invoicingExportUrl({ ...params, mode: "ar" }); }}
          title="Download AR (lien dates, notes, % complete) as a spreadsheet" aria-label={tr("Download AR")} data-testid="btn-export-invoices">
          <Download className="w-3.5 h-3.5" />
        </button>
        <div className="ml-auto text-xs text-slate-500 num" data-testid="invoicing-toolbar-count">
          {total} invoice{total === 1 ? "" : "s"} · {money((closed ? summary?.row_total : summary?.open_total) ?? 0)}{closed ? "" : " due"}
        </div>
      </div>
      </StickyTop>

      <div className="data-table-container">
        {selected.length > 0 && (
          <div className="px-3 py-2 bg-muted/40 border-b border-slate-200 flex items-center gap-3 text-sm" data-testid="invoicing-bulk-bar">
            <span className="text-xs font-medium"><span className="num">{selected.length}</span> selected</span>
            <button type="button" className={btn("data")} onClick={() => setBulkOpen(true)} disabled={!canWrite} data-testid="btn-bulk-reminder">
              <BellRing className="w-3.5 h-3.5" /> {tr("Send reminders")}
            </button>
            <button type="button" className="ml-auto text-xs text-slate-500 underline" onClick={() => setSelected([])} data-testid="btn-clear-selection">{tr("Clear")}</button>
          </div>
        )}

        <div className="overflow-x-auto">
          <Table {...gridNavProps}>
            <TableHeader className="thead-sticky">
              <TableRow>
                <TableHead className="w-8">
                  <input type="checkbox" checked={allSelected} className="h-3.5 w-3.5 accent-brand"
                    onChange={(e) => setSelected(e.target.checked ? rows.map(r => r.id) : [])}
                    data-testid="checkbox-select-all-invoices" />
                </TableHead>
                {activeColumns.map(c => (
                  <TableHead key={c.key} className={`text-xs whitespace-nowrap ${c.right ? "text-right" : ""} ${c.key === "lien_watch" ? "w-6 px-1" : ""} ${c.key === "ar_notes" && !showNotes ? "text-center" : ""}`} title={c.title}>
                    {c.noSort ? <span className="relative inline-flex items-center gap-1 font-semibold" data-testid={`col-${c.key}`}>{tr(c.label)}
                      {c.key === "ar_notes" && <span className={showNotes ? "" : "absolute left-full ml-0.5"}><ColToggle open={showNotes} onClick={() => setShowNotes(v => !v)} label="notes" testId="toggle-notes-column" /></span>}</span> : (
                      <SortableHeader label={tr(c.label)} sorted={sort === c.key} direction={dir} onSort={() => toggleSort(c.key)} testId={`sort-${c.key}`}
                        className={c.right ? "w-full justify-end" : ""}
                        filter={c.key === "status" ? <HeaderFilter label={tr("Status")} testId="filter-invoicing-status" options={statusHeaderOptions} value={status} onChange={setStatus} />
                          : c.key === "lien_status" ? <><HeaderFilter label={tr("Lien Status")} multi testId="filter-invoicing-lien-status" options={LIEN_STATUS_OPTIONS} value={lienStatus} onChange={setLienStatus} />
                            <ColToggle open={showLiens} onClick={() => setShowLiens(v => !v)} label="lien columns" testId="toggle-lien-columns" /></>
                          : c.key === "customer_name" ? <HeaderFilter label={tr("Customer")} testId="filter-invoicing-customer" options={customerHeaderOptions} value={customerId} onChange={setCustomerId} /> : undefined} />
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow><TableCell colSpan={colSpan} className="text-center text-sm text-slate-400 py-10" data-testid="invoicing-loading">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />{tr("Loading invoices…")}
                </TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={colSpan} className="text-center text-sm text-slate-400 py-10" data-testid="invoicing-empty">
                  {tr("No invoices match these filters.")}
                </TableCell></TableRow>
              ) : groups.map((g) => (
                <Fragment key={g.key || "all"}>
                  {g.label && (
                    <TableRow className="bg-muted/40 hover:bg-muted/40" data-testid={`invoicing-group-header-${g.key}`}>
                      <TableCell colSpan={colSpan} className={`py-1.5 text-xs font-semibold ${BUCKET_HEADER[g.key] || "text-slate-600"}`}>
                        {tr(g.label)}
                        <span className="ml-2 font-normal text-slate-400 num">{g.rows.length} · {money(g.rows.reduce((s, r) => s + (r.balance || 0), 0))}</span>
                      </TableCell>
                    </TableRow>
                  )}
                  {g.rows.map(r => (
                <TableRow key={r.id} data-testid={`invoice-row-${r.id}`} className={r.status === "void" ? "opacity-60" : ""}>
                  <TableCell>
                    <input type="checkbox" checked={selected.includes(r.id)} className="h-3.5 w-3.5 accent-brand"
                      onChange={(e) => setSelected(s => e.target.checked ? [...s, r.id] : s.filter(x => x !== r.id))}
                      data-testid={`checkbox-invoice-${r.id}`} />
                  </TableCell>
                  <TableCell className="px-1"><LienFlag row={r} onClick={showLiens ? undefined : () => setShowLiens(true)} /></TableCell>
                  <TableCell className="font-mono text-xs font-semibold max-w-[96px]">
                    <button type="button" className="block w-full truncate text-left text-brand hover:underline" onClick={() => openWO(r, true)}
                      title={r.invoice_number} data-testid={`link-invoice-${r.id}`} data-grid-enter="1">{r.invoice_number}</button>
                  </TableCell>
                  <TableCell className="text-right text-xs text-slate-600 num whitespace-nowrap">{shortDate(r.invoice_date)}</TableCell>
                  <TableCell className="text-xs text-slate-800 min-w-[104px] max-w-[150px]">
                    <button type="button" className="block w-full truncate text-left hover:underline" title={r.customer_name} onClick={() => navigate("/clients", { state: { openClientId: r.customer_id } })}
                      data-testid={`link-customer-${r.id}`} data-grid-enter="1">{r.customer_name || "—"}</button>
                  </TableCell>
                  <TableCell className="text-xs text-slate-600 whitespace-nowrap" data-testid={`ar-pm-${r.id}`}>{r.pm_short || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-700 max-w-[118px] truncate">
                    <button type="button" className="hover:underline text-left truncate block w-full" title={r.work_order_label}
                      onClick={() => openWO(r, false)} data-testid={`ar-wo-${r.id}`} data-grid-enter="1">{r.job_name || r.job_number || "—"}</button>
                  </TableCell>
                  <TableCell className="text-right text-xs font-medium num" data-testid={`ar-amount-${r.id}`}>{money(r.amount)}</TableCell>
                  <TableCell className="text-right text-xs num">
                    {(() => {
                      const fp = fpaid[r.foundation_ar_invoice_no || r.foundation_invoice_number];
                      const paid = fp ? fp.paid : (r.amount_paid || 0);
                      const shorted = paid > 0 && r.balance > 0;
                      const title = paid > 0 ? `Paid to date ${money(paid)}${fp ? ` (Foundation, as of ${String(fp.as_of).slice(0, 10)})` : " (recorded in the app)"} · balance due ${money(r.balance)}` : "No payment yet";
                      return <span className={shorted ? "text-rose-600 font-semibold" : ""} title={title} data-testid={`ar-balance-${r.id}`} data-shorted={shorted ? "1" : "0"}>{money(r.balance)}</span>;
                    })()}
                  </TableCell>
                  <TableCell className="text-right text-xs num text-slate-600" data-testid={`ar-retainage-${r.id}`}>{fmtRetainage(r)}</TableCell>
                  <TableCell className="text-right text-xs num" data-testid={`ar-age-${r.id}`}><span className={ageTone(r.invoice_age_days)}>{r.invoice_age_days ?? "—"}</span></TableCell>
                  <TableCell>
                    <Badge variant="outline"
                      className={`inline-flex min-w-[56px] justify-center px-1.5 text-[10px] whitespace-nowrap ${r.payment_state === "partial" ? "bg-amber-50 text-amber-700 border-amber-200" : (STATUS_PILL[r.status] || "")}`}
                      title={r.overdue ? `${r.days_past_due} days past due (due ${shortDate(r.due_date)})` : r.due_date ? `Due ${shortDate(r.due_date)}` : r.status === "draft" ? "Not sent yet — due date set when sent" : "No due date"}
                      data-testid={`status-${r.id}`}>{r.payment_state === "partial" ? "Partial" : (STATUS_LABEL[r.status] || r.status)}</Badge>
                  </TableCell>
                  <TableCell className={showNotes ? "" : "text-center"}><InvoiceNotesCell row={r} expanded={showNotes} by={user?.name || user?.email || ""} onChanged={() => load(params)} /></TableCell>
                  <RowActions r={r} canWrite={canWrite} onRemind={remind} onPayment={setPaymentTarget} onChanged={() => load(params)} />
                  <TableCell><LienCell row={r} onChanged={() => load(params)} disabled={!canWrite} onOpen={canWrite ? setChecklistTarget : undefined} /></TableCell>
                  {showLiens && <>
                  <TableCell className="text-right">
                    <InlineDate value={r.last_work_date} auto={r.last_work_date_auto} testId={`ar-last-day-${r.id}`}
                      title="Last day labor and/or material provided for lien rights — auto from the last onsite day or delivery once the job is complete; click to override"
                      onChange={(v) => patchRow(r, { last_work_date_override: v || "" })} />
                  </TableCell>
                  <TableCell className="text-right">
                    <InlineDate value={r.intent_due} auto={r.intent_due_auto} testId={`ar-intent-${r.id}`}
                      title="File intent 10 days prior to lien — auto from the lien date; click to override"
                      onChange={(v) => patchRow(r, { intent_due_override: v || "" })} />
                  </TableCell>
                  <TableCell className="text-right">
                    <InlineDate value={r.lien_date} auto={r.lien_date_auto} testId={`ar-lien-${r.id}`}
                      title="File lien 4 months after last day of work — auto from the last day; click to override (intent and enforce follow)"
                      onChange={(v) => patchRow(r, { lien_date_override: v || "" })} />
                  </TableCell>
                  <TableCell className="text-right">
                    <InlineDate value={r.enforce_date} auto={r.enforce_date_auto} testId={`ar-enforce-${r.id}`}
                      title="Enforce within 60 days of the lien date — auto from the lien date; click to override"
                      onChange={(v) => patchRow(r, { enforce_date_override: v || "" })} />
                  </TableCell>
                  <TableCell className="text-right">
                    <InlineDate value={r.completion_date} testId={`ar-completion-${r.id}`} title="Completion date — click to set"
                      onChange={(v) => patchRow(r, { completion_date: v || "" })} />
                  </TableCell>
                  <TableCell className="text-right text-xs num text-slate-600" data-testid={`ar-pct-${r.id}`}>{r.pct_complete}%</TableCell>
                  <LienActions r={r} canWrite={canWrite} closed={closed} onEmail={setLienTarget} onChecklist={setChecklistTarget} onChanged={() => load(params)} />
                  </>}
                </TableRow>
                  ))}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="p-3 flex items-center justify-between text-xs text-slate-500 border-t border-slate-200">
          <span data-testid="invoicing-count" className="num">
            {total === 0 ? "No invoices" : `Showing ${(page - 1) * effectivePageSize + 1}–${Math.min(page * effectivePageSize, total)} of ${total}`}
          </span>
          <div className="flex items-center gap-2">
            <button type="button" className={btn("nav")} disabled={page <= 1}
              onClick={() => setPage(p => p - 1)} data-testid="btn-invoicing-prev">{tr("Previous")}</button>
            <button type="button" className={btn("nav")} disabled={page * effectivePageSize >= total}
              onClick={() => setPage(p => p + 1)} data-testid="btn-invoicing-next">{tr("Next")}</button>
          </div>
        </div>
      </div>

      {bulkOpen && (
        <BulkReminderDialog
          rows={selectedRows}
          onClose={() => setBulkOpen(false)}
          onDone={() => { setBulkOpen(false); setSelected([]); load(params); }}
        />
      )}

      {paymentTarget && (
        <PaymentDialog
          row={paymentTarget}
          onClose={() => setPaymentTarget(null)}
          onChanged={() => load(params)}
        />
      )}

      {lienTarget && (
        <LienReminderDialog row={lienTarget} onClose={() => setLienTarget(null)} onSent={() => load(params)} />
      )}

      {checklistTarget && (
        <LienChecklistDialog row={checklistTarget} by={user?.name || user?.email || ""} onClose={() => setChecklistTarget(null)} onSaved={() => load(params)} />
      )}
    </div>
  );
}

function BulkReminderDialog({ rows, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const eligible = rows.filter(r => r.status === "sent");
  const skipped = rows.filter(r => r.status !== "sent");

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await bulkInvoiceReminder({ invoice_ids: eligible.map(r => r.id) });
      toast.success(`${data.sent} reminder${data.sent === 1 ? "" : "s"} sent${data.failed ? `, ${data.failed} failed` : ""}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="dialog-bulk-reminder">
        <DialogHeader>
          <DialogTitle>Send {eligible.length} payment reminder{eligible.length === 1 ? "" : "s"}</DialogTitle>
          <DialogDescription>Each invoice gets its own reminder email, logged exactly like a single send from the work order.</DialogDescription>
        </DialogHeader>
        <div className="max-h-64 overflow-y-auto text-sm space-y-1">
          {eligible.map(r => (
            <div key={r.id} className="flex justify-between gap-2 border-b border-slate-100 pb-1" data-testid={`bulk-row-${r.id}`}>
              <span className="font-mono text-xs">{r.invoice_number}</span>
              <span className="truncate text-slate-600">{r.customer_email || "no email on file"}</span>
              <span className="tabular-nums">{money(r.balance)}</span>
            </div>
          ))}
          {skipped.length > 0 && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mt-2" data-testid="bulk-skipped">
              {skipped.length} skipped — reminders only go to invoices that have been sent
              ({skipped.map(r => r.invoice_number).join(", ")}).
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="btn-cancel-bulk-reminder">{tr("Cancel")}</Button>
          <Button onClick={send} disabled={busy || eligible.length === 0} data-testid="btn-confirm-bulk-reminder">
            {busy ? "Sending…" : `Send ${eligible.length}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

